import moment from 'moment'
import _ from 'lodash'
import { utils } from 'frontend-js-shared'

import appConstants from '@/constants'
import filtersConstants from './constants'
import store from '@/store'

/*
this function is used to generate a payload to make a request to backend, this transform an array of this form: 
[
  {
    key: 'status', 
    value: ['open', 'completed'],
    OTHER_PROPERTIES_OF_FILTER (name, multiselect, etc),
  },
  {
    key: 'role'
    value: 'owner',
    OTHER_PROPERTIES_OF_FILTER (name, multiselect, etc),
  },
]
to an object of this form: 
{
  status: ['open', 'completed'],
  owner: USER_ID
}
this function needs an array as described above and the user account to fill values in specifc cases as in role filter
*/
export function serialize(deserializedFilters = []) {
  return deserializedFilters.reduce((result, filter) => {
    if (filter.key === filtersConstants.filtersKeys.condition) {
      return {
        ...result,
        ...getConditionRequestFilters(filter.value),
      }
    }

    if (filter.key === filtersConstants.filtersKeys.approval) {
      return {
        ...result,
        ...getApprovalRequestFilters(filter.value),
      }
    }

    if (filter.key === filtersConstants.filtersKeys.activeRoutine) {
      return {
        ...result,
        ...getActiveRoutineRequestFilters(filter.value),
      }
    }

    if (filter.key === filtersConstants.filtersKeys.routineType) {
      return {
        ...result,
        ...getRoutineTypeRequestFilters(filter.value),
      }
    }

    if (filter.key === filtersConstants.filtersKeys.validation) {
      return {
        ...result,
        ...getValidationRequestFilters(filter.value),
      }
    }

    if (filter.key === filtersConstants.filtersKeys.role) {
      const accountId = store.state.access.account.id
      return {
        ...result,
        ...getRoleRequestFilters(filter.value, accountId),
      }
    }

    if (
      filter.key === filtersConstants.filtersKeys.dueDateOption ||
      filter.key === filtersConstants.filtersKeys.createdAtOption ||
      filter.key === filtersConstants.filtersKeys.completedAtOption
    ) {
      return {
        ...result,
        [filtersConstants.dateFilters[filter.key]]: formatDates(
          getDateRequestFilter(filter)
        ),
        [filter.key]: filter.value,
      }
    }

    if (filter.key === filtersConstants.filtersKeys.createdBy) {
      return {
        ...result,
        createdBy: filter.value,
      }
    }

    return {
      ...result,
      [filter.key]: _.isArray(filter.value)
        ? filter.value?.map(value => value?.id ?? value)
        : filter.value,
    }
  }, {})
}

function getActiveRoutineRequestFilters(value) {
  if (value === 'active') return { isActive: true }
  if (value === 'inactive') return { isActive: false }
}

function getRoutineTypeRequestFilters(value) {
  return { type: value }
}

function getConditionRequestFilters(values = []) {
  return Object.values(filtersConstants.conditionFilters).reduce(
    (result, condition) => {
      if (!values.includes(condition)) return result

      if (condition === filtersConstants.conditionFilters.isNotRevFromFinding) {
        return {
          ...result,
          [filtersConstants.conditionFilters.isRevFromFinding]: false,
        }
      }

      if (
        condition === filtersConstants.conditionFilters.isRevFromFinding &&
        values.includes(filtersConstants.conditionFilters.isNotRevFromFinding)
      ) {
        return result
      }

      return {
        ...result,
        [condition]: true,
      }
    },
    {}
  )
}

function getValidationRequestFilters(value) {
  switch (value) {
    case filtersConstants.validationFilters.implemented: {
      return {
        [filtersConstants.validationFilters.implemented]: true,
        [filtersConstants.validationFilters.validated]: false,
      }
    }

    case filtersConstants.validationFilters.notImplemented: {
      return {
        [filtersConstants.validationFilters.implemented]: false,
      }
    }

    case filtersConstants.validationFilters.validated: {
      return {
        [filtersConstants.validationFilters.validated]: true,
      }
    }

    default:
      return {}
  }
}

function getApprovalRequestFilters(values = []) {
  const accountId = store.state.access.account.id
  return values.reduce((result, value) => {
    if (value === filtersConstants.approvalFilters.approved) {
      return {
        ...result,
        isApproved: true,
      }
    }

    if (value === filtersConstants.approvalFilters.requested) {
      return {
        ...result,
        isApprovalRequested: true,
      }
    }

    if (value === filtersConstants.approvalFilters.waitingForMyApproval) {
      return {
        ...result,
        waitingForMyApproval: [accountId],
      }
    }

    if (value === filtersConstants.approvalFilters.rejected) {
      return {
        ...result,
        isRejected: true,
      }
    }

    return result
  }, {})
}

function getRoleRequestFilters(value, accountId) {
  switch (value) {
    case filtersConstants.roleFilters.collaborator: {
      return {
        collaborators: [accountId],
      }
    }

    case filtersConstants.roleFilters.owner: {
      return {
        owner: accountId,
      }
    }

    case filtersConstants.roleFilters.coach:
      return {
        coach: accountId,
      }

    default:
      return {}
  }
}

function getDateRequestFilter(data) {
  if (!data.value) return {}

  if (data.value === appConstants.DATE_FILTER_OPTIONS.custom) {
    return data?.dates?.length
      ? {
          from: getStartOfDay(data.dates[0]),
          to: getEndOfDay(data.dates[1]),
        }
      : {}
  }

  const { startDate, endDate } = utils.dates.getPredefinedDateRange(data.value)

  return {
    from: getStartOfDay(startDate),
    to: getEndOfDay(endDate),
  }
}

function formatDates(dates = {}) {
  if (_.isEmpty(dates)) return {}

  return {
    from: moment(dates.from).format(),
    to: moment(dates.to)
      .endOf('day')
      .format(),
  }
}

function getStartOfDay(date) {
  const currentTzOffset = moment().utcOffset()
  return moment(date)
    .startOf('day')
    .utcOffset(currentTzOffset)
    .format()
}

function getEndOfDay(date) {
  const currentTzOffset = moment().utcOffset()
  return moment(date)
    .endOf('day')
    .utcOffset(currentTzOffset)
    .format()
}
